import { load } from '@fingerprintjs/botd';
import { ApiClient, type ApiClientOptions, type AuthTokens } from '@tunein/web-api-client';
import { Singleton, type SingletonInstance, safeJsonStringify } from '@tunein/web-utils';
import { storeSingleton } from 'src/client/store/storeSingleton';
import { player, playerClick } from 'src/common/constants/reporting/sandboxCategoryActionProps';
import { selectDiscordState } from 'src/common/selectors/app';

export class ApiSingleton extends Singleton<ApiClient> {
  init(options: ApiClientOptions) {
    this.instance = new ApiClient(options);

    this.#reportBotDetection();
  }

  async loginDesktop(token: string) {
    const authTokens = await api.fetch<AuthTokens>({
      url: '/api/v1/auth/loginDesktop/',
      json: { token },
    });

    await api.setAuthTokens(authTokens);

    return authTokens;
  }

  reportPlayerError(context: any) {
    const {
      playerName = '',
      streamId = '',
      url = '',
      format = '',
      error = {},
      originalErrorMessage = '',
      wasInSteadyState,
    } = context || {};

    api.telemetry.reportSandbox({
      category: player.category,
      action: player.actions.error,
      props: {
        playerName,
        streamId,
        url,
        format,
        errorData: safeJsonStringify(error.data),
        errorMessage: error.message || originalErrorMessage || 'Unknown error',
        errorType: error.type || 'Unknown',
        isMediaError: error.isMediaError?.toString() || '',
        isNetworkError: error.isNetworkError?.toString() || '',
        wasInSteadyState: wasInSteadyState?.toString() || 'false',
      },
    });
  }

  reportPlayerStopClick() {
    const discordState = selectDiscordState(storeSingleton.state);

    if (!discordState.canControlPlayback) {
      return;
    }

    return api.telemetry.reportSandbox({
      category: playerClick.category,
      action: playerClick.actions.stop,
      props: {},
    });
  }

  reportPlayerPauseClick() {
    const discordState = selectDiscordState(storeSingleton.state);

    if (!discordState.canControlPlayback) {
      return;
    }

    api.telemetry.reportSandbox({
      category: playerClick.category,
      action: playerClick.actions.pause,
      props: {},
    });
  }

  async #reportBotDetection() {
    try {
      const botd = await load({ monitoring: false });
      const result = await botd.detect();

      api.telemetry.reportSandbox({
        category: 'botd',
        action: 'detect',
        props: {
          isBot: String(result.bot),
        },
      });
    } catch (error) {
      api.telemetry.logError({
        message: 'Bot detection or reporting failed',
        context: {
          error,
        },
      });
    }
  }
}

export const api = new ApiSingleton() as SingletonInstance<ApiClient, ApiSingleton>;
